import { useEffect, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { LOADER_APPEAR_AFTER_MS } from './useLoadPhase';

/**
 * The CRM's loading indicator — the partner portal's `PartnerSpinner`
 * (partner/components/common), kept in step with it but coloured from the MUI
 * theme's `primary` (Zenvoya purple) instead of the partner brand variable.
 *
 * Paired with `useLoadPhase` (first load → this ring; reload → shimmer rows from
 * `TableSkeletonRows`) and used as the CRM route fallback.
 *
 * WHY THE VALUE IS SYNTHETIC
 * `variant="determinate"` needs a number, and there is no honest one to give.
 * Every partner call is a single encrypted POST through portalClient with no
 * download-progress hook, and the payloads are small enough that real byte
 * progress would jump 0→100 with nothing in between. So the ring is driven by a
 * timer instead: it eases towards CEILING and never reaches it, because the only
 * event that could justify 100% is the response landing — and when that happens
 * the caller unmounts this component. A ring that filled completely and then sat there
 * would be claiming the load had finished while it plainly had not.
 *
 * WHY IT HIDES ITSELF FIRST
 * `specClient.settle()` resolves fixtures in 120ms and a warm live call is not
 * much slower. Painting a ring for two frames reads as a flicker, so nothing is
 * rendered for the first LOADER_APPEAR_AFTER_MS. Below that threshold the user simply
 * sees the new data arrive, which is the correct outcome, not a missing loader.
 *
 * WHY IT GIVES UP AND SPINS
 * A determinate ring does not rotate. Once the ramp is close to its ceiling the
 * arc stops visibly moving, and a still ring is the most recognisable "this has
 * hung" signal there is — users refresh the page. That matters here because
 * postToBackend can take far longer than one round trip: it may insert a
 * proactive session refresh before the call and a second refresh plus retry
 * after a 401. So past HANDOVER_MS the component stops pretending to know and
 * switches to the indeterminate ring, which keeps moving and claims nothing.
 */

/* Never reached — see the note above about what 100% would be claiming. */
const CEILING = 92;
/* Past this the ramp has run out of honest things to say — hand over to the
   indeterminate ring rather than let a near-static arc read as a hang. */
const HANDOVER_MS = 1600;
/* Coarse enough to be cheap, fine enough that the ring reads as moving. */
const TICK_MS = 90;
/* Fraction of the remaining distance covered per tick. Deceleration falls out of
   this for free: each tick advances less than the one before, so the ring moves
   confidently at the start and crawls once it is close to the ceiling. */
const APPROACH = 0.14;
/* Where the ring starts once it appears. Starting from 0 wastes the first tick
   on an arc too short to see. */
const START_AT = 12;

export default function PortalSpinner({ size = 36, thickness = 4, style }) {
  const [value, setValue] = useState(START_AT);
  const [visible, setVisible] = useState(false);
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    const appearTimer = setTimeout(() => setVisible(true), LOADER_APPEAR_AFTER_MS);
    const handoverTimer = setTimeout(() => setStalled(true), HANDOVER_MS);
    const tick = setInterval(() => {
      setValue((prev) => prev + (CEILING - prev) * APPROACH);
    }, TICK_MS);
    return () => {
      clearTimeout(appearTimer);
      clearTimeout(handoverTimer);
      clearInterval(tick);
    };
  }, []);

  if (!visible) return null;

  return (
    <CircularProgress
      variant={stalled ? 'indeterminate' : 'determinate'}
      value={value}
      size={size}
      thickness={thickness}
      /* `inherit`, so the SVG's `stroke: currentColor` resolves off the sx
         colour below. */
      color="inherit"
      /* The ring reports role="progressbar" with aria-valuenow, and that
         number is invented, so keep it out of the accessibility tree. */
      aria-hidden="true"
      sx={{ color: 'primary.main', display: 'block' }}
      style={style}
    />
  );
}
